import React, { FC, useMemo } from "react";
import {
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { TimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FormFieldProps } from "../../../utils/formFields";
import useStyles from "../../../components/common/model/styles";
import dayjs from "../../../utils/dayjsConfig";
import type { Dayjs } from "dayjs"; // ✅ type import
import { useSelector } from "react-redux";

export interface TimeRangeValue {
  start: string | null; // ISO string (UTC)
  end: string | null; // ISO string (UTC)
}

export interface FormTimeRangeFieldProps extends Omit<
  FormFieldProps,
  "value" | "onChange"
> {
  value?: TimeRangeValue | null;
  onChange?: (event: {
    target: { id?: string; value: TimeRangeValue };
  }) => void;
  // Optional custom labels for the two sub-fields
  startLabel?: string;
  endLabel?: string;
}

const FormTimeRangeField: FC<FormTimeRangeFieldProps> = (props) => {
  const classes = useStyles();
  const { editClientOriginalData } = useSelector(
    (state: any) => state.adminClients,
  );

  const clientTimeZone = editClientOriginalData?.timezone || "UTC";

  const startValue = props.value?.start
    ? dayjs.utc(props.value.start).tz(clientTimeZone)
    : null;
  const endValue = props.value?.end
    ? dayjs.utc(props.value.end).tz(clientTimeZone)
    : null;

  // End time is invalid if it falls before (or equals) the start time
  const rangeError = useMemo(() => {
    if (!startValue || !endValue) return false;
    return !endValue.isAfter(startValue);
  }, [startValue, endValue]);

  const emitChange = (next: TimeRangeValue) => {
    props?.onChange?.({ target: { id: props.id, value: next } });
  };

  const toUtcIso = (date: Dayjs | null): string | null => {
    if (!date) return null;
    try {
      const clientDate = date.tz(clientTimeZone, true); // keep local time, reinterpret in client tz
      return clientDate.utc().toISOString();
    } catch {
      return null;
    }
  };

  const handleStartChange = (date: Dayjs | null) => {
    emitChange({
      start: toUtcIso(date),
      end: props.value?.end ?? null,
    });
  };

  const handleEndChange = (date: Dayjs | null) => {
    emitChange({
      start: props.value?.start ?? null,
      end: toUtcIso(date),
    });
  };

  const hasError = !!props.error || rangeError;
  const helperText = props.error
    ? props.helperText
    : rangeError
      ? "End time must be after start time"
      : props.helperText;

  const pickerSx = {
    width: "100%",
    background: "#fff",
    "&.MuiFormControl-root": { borderRadius: "5px" },
  };

  const inputRender = () => (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" alignItems="flex-start" sx={{ width: "100%" }}>
        <Stack sx={{ flex: 1 }}>
          {props.startLabel && (
            <Typography sx={{ fontSize: "13px", color: "#637381", mb: 0.5 }}>
              {props.startLabel}
            </Typography>
          )}
          <TimePicker
            sx={pickerSx}
            disabled={props?.disabled}
            ampm={false}
            value={startValue}
            onChange={handleStartChange}
            slotProps={{
              textField: {
                id: props.id ? `${props.id}-start` : undefined,
                name: props.id ? `${props.id}-start` : undefined,
                size: "small",
                error: hasError,
                onBlur: () => props?.onBlur?.({ target: { id: props.id } }),
                InputProps: { sx: { height: "36px" } },
              },
            }}
          />
        </Stack>
        <Typography
          sx={{
            fontSize: "16px",
            color: "#637381",
            px: "6px",
            pb: "0px",
            mt: "6px",
            flexShrink: 0,
            fontWeight: 600,
          }}
        >
          -
        </Typography>
        <Stack sx={{ flex: 1 }}>
          {props.endLabel && (
            <Typography sx={{ fontSize: "13px", color: "#637381", mb: 0.5 }}>
              {props.endLabel}
            </Typography>
          )}
          <TimePicker
            sx={pickerSx}
            disabled={props?.disabled}
            ampm={false}
            value={endValue}
            minTime={startValue || undefined}
            onChange={handleEndChange}
            slotProps={{
              textField: {
                id: props.id ? `${props.id}-end` : undefined,
                name: props.id ? `${props.id}-end` : undefined,
                size: "small",
                error: hasError,
                onBlur: () => props?.onBlur?.({ target: { id: props.id } }),
                InputProps: {
                  sx: {
                    height: "36px",
                    border: hasError ? "1px solid #d32f2f" : undefined,
                  },
                },
              },
            }}
          />
        </Stack>
      </Stack>

      {hasError && (
        <FormHelperText sx={{ color: "#d32f2f", marginLeft: "14px" }}>
          {helperText}
        </FormHelperText>
      )}
    </LocalizationProvider>
  );

  return (
    <>
      {props?.label && !props?.inSingleGridForm ? (
        <>
          <Grid
            item
            xs={12}
            sm={props?.md ? 1.5 : 5}
            className={classes.labelBox}
            sx={{ marginTop: props?.isNonMarginTop ? "0px !important" : "" }}
          >
            <InputLabel
              sx={{ color: "#333333", fontWeight: 600, fontSize: "18px" }}
              shrink
              htmlFor={props?.id}
            >
              {props?.label}
              {props?.isRequired && (
                <span style={{ color: "red", fontWeight: 500 }}>*</span>
              )}
            </InputLabel>
          </Grid>
          <Grid
            item
            xs={12}
            sm={props?.md ? props?.md : 7}
            style={{
              padding: "0px",
              marginTop: props?.isNonMarginTop ? "0px" : "16px",
              marginBottom: "10px",
            }}
          >
            {inputRender()}
          </Grid>
        </>
      ) : props?.label && props?.inSingleGridForm ? (
        <Grid
          item
          xs={12}
          sm={props?.md ? props?.md : 12}
          sx={{
            display: "block",
            alignItems: "center",
            gap: 2,
            ...props?.GridFieldStyle,
          }}
        >
          <Tooltip title={props?.tooltipText || ""} arrow>
            <InputLabel
              sx={{
                color: "#637381",
                fontWeight: 600,
                fontSize: "16px",
                textAlign: "left !important",
              }}
              shrink
              htmlFor={props?.id}
            >
              {props?.label}
              {props?.isRequired && (
                <span style={{ color: "red", fontWeight: 500 }}>*</span>
              )}
            </InputLabel>
          </Tooltip>
          {inputRender()}
        </Grid>
      ) : (
        inputRender()
      )}
    </>
  );
};

export default FormTimeRangeField;
